import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { $global } from 'models/store-global';
import { Spinner } from 'react-bootstrap';

import { DocDropdownOption } from './document-page';

import Dropdown from 'components/StyledTemplates/Dropdown';
import { Option } from 'components/Table/Table';
import { Document, MoreButton } from 'components/pages/Dashboard/common/CommunityPageStyles';

import DocumentDownloadService from 'services/DocumentDownloadService';
import DocumentsService from 'services/DocumentsService';
import ModalsService from 'services/ModalsService';

import useResources from 'hooks/useResources';

import { type Document as MyAdvocateDocument } from 'types/zod/document';

interface DocumentBlockProps {
    document: MyAdvocateDocument;
    isSigned: boolean;
    options: DocDropdownOption[];
    headerColor?: any;
    allowScanUpload?: boolean;
    showStatus?: boolean;
    onNameClick?: () => void;
}

const DocumentBlock = (props: DocumentBlockProps) => {
    const { document, isSigned, options, headerColor, allowScanUpload, onNameClick, showStatus = true } = props;

    const isUploading = useUnit($global.map((state) => state.uploading?.[props.document.document_id] || false));

    const { t } = useResources();

    const getDocumentStatusLabel = (document: MyAdvocateDocument) => {
        if (!DocumentsService.isSignable(document)) {
            return t('document_status_code.Completed');
        } else if (isSigned) {
            return t('document_status_code.Signed');
        } else {
            return t('document_status_code.Ready_to_Sign');
        }
    };

    return (
        <Document.Card
            headerColor={headerColor}
            onClick={(event) => {
                const element = event.target as HTMLDivElement;
                if (!element.classList.contains('ignore-row-click')) {
                    if (
                        document.document_type_code === 'MAD_Instr' ||
                        document.document_type_code === 'MAD_Instr_Spouse'
                    ) {
                        DocumentDownloadService.downloadDocx(document.document_id);
                    } else {
                        DocumentDownloadService.download(document.document_id);
                    }
                }
            }}
        >
            <Document.Name
                onClick={() => {
                    onNameClick && onNameClick();
                }}
                extra={onNameClick ? 'cursor: pointer' : ''}
            >
                {DocumentsService.getShortName(document)}
            </Document.Name>
            {showStatus && (
                <Document.Description>
                    <span>
                        {`${getDocumentStatusLabel(document)}:`}
                        <br />
                        {dayjs(document?.creation_date ?? 0).format('MM/DD/YYYY')}
                    </span>
                </Document.Description>
            )}
            {allowScanUpload && (document?.scan_names || []).length > 0 && (
                <Document.ScansButton
                    className="ignore-row-click"
                    onClick={() => {
                        ModalsService.showModal('DASHBOARD_SCANS', document);
                    }}
                >
                    {t('newDashboardPage.scansModal.dashboardHandlerLabel')}
                </Document.ScansButton>
            )}
            {isUploading && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
            <Document.DropdownWrapper>
                <Dropdown
                    className="ignore-row-click"
                    toggle={<MoreButton />}
                    menu={
                        <>
                            {options.map((option, index) => (
                                <Option key={index} {...option} extra="width: -webkit-fill-available;" />
                            ))}
                        </>
                    }
                    menuStyles="align-items: flex-start;"
                />
            </Document.DropdownWrapper>
        </Document.Card>
    );
};

export default DocumentBlock;
